import React from "react";
import { Bar, Doughnut, HorizontalBar, Line, Scatter } from "react-chartjs-2";

import { CHART_PALETTE } from "../utils/chartColors.js";
import { PROJECT_CHART_FONT, PROJECT_CHART_FONT_SIZE, PROJECT_CHART_PADDING } from "../utils/chartStyles.js";

const numericValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const normalizeHeight = (height) => {
  const number = Number(height);
  return Number.isFinite(number) ? `${number}px` : (height || "300px");
};

const tooltipFormatter = (options) => {
  const tooltipY = options?.tooltip?.y;
  if (typeof tooltipY?.formatter === "function") return tooltipY.formatter;
  if (Array.isArray(tooltipY) && typeof tooltipY[0]?.formatter === "function") return tooltipY[0].formatter;
  return (value) => value;
};

const chartLabels = (options, series) => {
  if (Array.isArray(options?.labels)) return options.labels;
  if (Array.isArray(options?.xaxis?.categories)) return options.xaxis.categories;
  const firstData = Array.isArray(series?.[0]?.data) ? series[0].data : [];
  return firstData.map((point, index) => point?.x ?? index + 1);
};

const commonOptions = (options, horizontal = false) => {
  const formatTooltip = tooltipFormatter(options);
  const formatTooltipTitle = options?.tooltip?.x?.formatter;
  const yTitle = options?.yaxis?.title?.text;
  const xTitle = options?.xaxis?.title?.text;
  const xFormatter = options?.xaxis?.labels?.formatter;
  const yFormatter = options?.yaxis?.labels?.formatter;
  const gridColor = options?.grid?.borderColor || "#e9ecef";
  const stacked = options?.chart?.stacked === true;
  const isBar = Boolean(options?.plotOptions?.bar);

  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { ...PROJECT_CHART_PADDING },
    },
    legend: {
      display: options?.legend?.show !== false,
      position: options?.legend?.position || "top",
      labels: { fontFamily: PROJECT_CHART_FONT, fontSize: PROJECT_CHART_FONT_SIZE },
    },
    tooltips: {
      mode: options?.tooltip?.shared === false ? "nearest" : "index",
      intersect: options?.tooltip?.intersect === true,
      titleFontFamily: PROJECT_CHART_FONT,
      bodyFontFamily: PROJECT_CHART_FONT,
      footerFontFamily: PROJECT_CHART_FONT,
      titleFontSize: PROJECT_CHART_FONT_SIZE,
      bodyFontSize: PROJECT_CHART_FONT_SIZE,
      callbacks: {
        ...(typeof formatTooltipTitle === "function" ? {
          title: (items) => {
            const item = items?.[0];
            return formatTooltipTitle(item?.xLabel, { dataPointIndex: item?.index });
          },
        } : {}),
        label: (tooltipItem, data) => {
          const dataset = data.datasets?.[tooltipItem.datasetIndex];
          const value = horizontal ? tooltipItem.xLabel : tooltipItem.yLabel;
          return `${dataset?.label ? `${dataset.label}: ` : ""}${formatTooltip(value)}`;
        },
      },
    },
    scales: {
      xAxes: [{
        stacked,
        ...(isBar && !horizontal ? { offset: true } : {}),
        gridLines: { color: gridColor },
        scaleLabel: { display: Boolean(xTitle), labelString: xTitle, fontFamily: PROJECT_CHART_FONT, fontSize: PROJECT_CHART_FONT_SIZE },
        ticks: {
          fontFamily: PROJECT_CHART_FONT,
          fontSize: PROJECT_CHART_FONT_SIZE,
          minRotation: options?.xaxis?.labels?.rotate || 0,
          maxRotation: options?.xaxis?.labels?.rotate || 0,
          beginAtZero: horizontal,
          padding: 8,
          autoSkip: options?.xaxis?.labels?.hideOverlappingLabels !== false,
          ...(options?.xaxis?.tickAmount ? { maxTicksLimit: options.xaxis.tickAmount } : {}),
          ...(horizontal ? { min: 0, precision: 0 } : {}),
          ...(typeof xFormatter === "function" ? { callback: xFormatter } : {}),
        },
      }],
      yAxes: [{
        stacked,
        gridLines: { color: gridColor },
        scaleLabel: { display: Boolean(yTitle), labelString: yTitle, fontFamily: PROJECT_CHART_FONT, fontSize: PROJECT_CHART_FONT_SIZE },
        ticks: {
          fontFamily: PROJECT_CHART_FONT,
          fontSize: PROJECT_CHART_FONT_SIZE,
          beginAtZero: !horizontal,
          padding: 8,
          ...(!horizontal ? { min: 0, precision: 0 } : {}),
          ...(Number.isFinite(Number(options?.yaxis?.max)) ? { max: Number(options.yaxis.max) } : {}),
          ...(typeof yFormatter === "function" ? { callback: yFormatter } : {}),
        },
      }],
    },
  };
};

const heatColor = (value, ranges) => {
  const match = ranges.find((range) => value >= numericValue(range.from) && value <= numericValue(range.to));
  return match?.color || "#f1f3f5";
};

function HeatmapChart({ options, series, height }) {
  const ranges = options?.plotOptions?.heatmap?.colorScale?.ranges || [];
  const labels = chartLabels(options, series);
  const data = {
    datasets: series.map((weekday, weekdayIndex) => ({
      label: weekday.name,
      data: (weekday.data || []).map((point, hourIndex) => ({
        x: hourIndex,
        y: weekdayIndex,
        activityValue: numericValue(point?.y),
      })),
      showLine: false,
      pointStyle: "rectRounded",
      pointRadius: 9,
      pointHoverRadius: 10,
      pointBackgroundColor: (weekday.data || []).map((point) => heatColor(numericValue(point?.y), ranges)),
      pointBorderColor: "rgba(255,255,255,.75)",
      pointBorderWidth: 1,
    })),
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { ...PROJECT_CHART_PADDING } },
    legend: { display: false, labels: { fontFamily: PROJECT_CHART_FONT, fontSize: PROJECT_CHART_FONT_SIZE } },
    tooltips: {
      titleFontFamily: PROJECT_CHART_FONT,
      bodyFontFamily: PROJECT_CHART_FONT,
      titleFontSize: PROJECT_CHART_FONT_SIZE,
      bodyFontSize: PROJECT_CHART_FONT_SIZE,
      callbacks: {
        title: (items) => labels[items?.[0]?.xLabel] || "",
        label: (item, chartData) => {
          const dataset = chartData.datasets?.[item.datasetIndex];
          const point = dataset?.data?.[item.index];
          return `${dataset?.label || ""}: ${point?.activityValue || 0} atividades`;
        },
      },
    },
    scales: {
      xAxes: [{
        type: "linear",
        ticks: { min: 0, max: 23, stepSize: 1, fontFamily: PROJECT_CHART_FONT, fontSize: PROJECT_CHART_FONT_SIZE, callback: (value) => labels[value] || "" },
        gridLines: { display: false },
      }],
      yAxes: [{
        type: "linear",
        ticks: {
          min: 0,
          max: Math.max(0, series.length - 1),
          stepSize: 1,
          fontFamily: PROJECT_CHART_FONT,
          fontSize: PROJECT_CHART_FONT_SIZE,
          callback: (value) => series[value]?.name || "",
        },
        gridLines: { display: false },
      }],
    },
  };

  return <div style={{ height: normalizeHeight(height), minWidth: 0, width: "100%" }}><Scatter data={data} options={chartOptions} /></div>;
}

export default function ChartJsChart({ options = {}, series = [], type = "line", height = 300 }) {
  if (type === "heatmap") return <HeatmapChart options={options} series={series} height={height} />;

  const colors = options.colors?.length ? options.colors : CHART_PALETTE;
  const labels = chartLabels(options, series);
  const horizontal = type === "bar" && options?.plotOptions?.bar?.horizontal === true;
  const distributed = options?.plotOptions?.bar?.distributed === true;
  const smooth = options?.stroke?.curve === "smooth";
  const lineWidth = numericValue(options?.stroke?.width ?? 3);

  if (type === "donut") {
    const data = {
      labels,
      datasets: [{
        data: series.map(numericValue),
        backgroundColor: labels.map((_, index) => colors[index % colors.length]),
        borderColor: "#fff",
        borderWidth: 2,
      }],
    };
    const donutOptions = commonOptions(options);
    delete donutOptions.scales;
    const formatValue = tooltipFormatter(options);
    donutOptions.tooltips.callbacks.title = () => "";
    donutOptions.tooltips.callbacks.label = (tooltipItem, chartData) => {
      const label = chartData.labels?.[tooltipItem.index] || "";
      const value = chartData.datasets?.[tooltipItem.datasetIndex]?.data?.[tooltipItem.index];
      return `${label}: ${formatValue(value)}`;
    };
    return <div style={{ height: normalizeHeight(height), minWidth: 0, width: "100%" }}><Doughnut data={data} options={donutOptions} /></div>;
  }

  const data = {
    labels,
    datasets: series.map((item, index) => {
      const color = colors[index % colors.length];
      const values = (item.data || []).map((point) => numericValue(point?.y ?? point));
      const configuredPointRadius = numericValue(options?.markers?.size ?? (type === "line" ? 2 : 0));
      return {
        label: item.name,
        data: values,
        borderColor: color,
        backgroundColor: distributed
          ? (item.data || []).map((_, dataIndex) => colors[dataIndex % colors.length])
          : color,
        borderWidth: type === "line" ? lineWidth : 0,
        fill: false,
        showLine: type === "line",
        spanGaps: true,
        lineTension: smooth ? 0.35 : 0,
        ...(type === "bar" ? { barPercentage: 0.62, categoryPercentage: 0.8 } : {}),
        pointRadius: type === "line"
          ? values.map((value) => (value > 0 ? configuredPointRadius : 0))
          : 0,
        pointHoverRadius: type === "line" ? 4 : 0,
      };
    }),
  };
  const convertedOptions = commonOptions(options, horizontal);
  const chart = horizontal
    ? <HorizontalBar data={data} options={convertedOptions} />
    : type === "bar"
      ? <Bar data={data} options={convertedOptions} />
      : <Line data={data} options={convertedOptions} />;

  return <div style={{ height: normalizeHeight(height), minWidth: 0, width: "100%" }}>{chart}</div>;
}
